import * as THREE from 'three';
import { shadowBlastEffect as LOOK } from '../../../content/spells/effects.js';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import { DarkSmoke, softGlowTexture } from './dark-smoke.js';
import { mulberry32 } from './blood-model.js';
import type { SpellLight } from './spell-effects.js';

const CORE = 9, TRAIL = LOOK.trail, BURST = 34, CLUTCH = 10;
/** Seconds a puff shed by the flying bolt lasts, and how many it sheds each second. */
const TRAIL_LIFE = .8, SHED = 60;
interface Puff { x: number; y: number; z: number; vx: number; vy: number; vz: number; born: number; life: number; size: number; seed: number }
interface Flight { smoke: DarkSmoke; halo: THREE.Sprite; from: THREE.Vector3; to: THREE.Vector3; head: THREE.Vector3; trail: Puff[]; shed: number; ended?: number; random: () => number; seed: number }
interface Burst { smoke: DarkSmoke; halo: THREE.Sprite; targetId: string; at: number; centre: THREE.Vector3; puffs: Puff[]; clutch: Puff[]; miss: boolean; duration: number }

/**
 * Shadow Blast, drawn: a tumbling knot of black smoke with a cold rim flies from the casting hands, shedding torn wisps that hang and thin
 * behind it; on a hit it bursts into smoke that billows out and claws back in round the target, on a miss it flies on past and comes apart.
 * Presentation only: the flight follows the server's projectile and the burst its impact or miss.
 */
export class ShadowBlastEffects {
  readonly lights: SpellLight[] = [];
  private flights = new Map<number, Flight>();
  private bursts: Burst[] = [];
  private pending: { event: CombatVisualEvent; at: number }[] = [];
  private warmup: DarkSmoke;
  get visualObjects(): THREE.Object3D[] { return [...[...this.flights.values()].flatMap(flight => [flight.smoke.mesh, flight.halo]), ...this.bursts.flatMap(burst => [burst.smoke.mesh, burst.halo])]; }
  constructor(private scene: THREE.Scene, private heights: Map<string, number>, private groundHeight: (x: number, z: number) => number = () => 0) {
    this.warmup = new DarkSmoke(1, LOOK.color, LOOK.rim, 'shadow-blast-warmup');
  }
  prepare() { this.scene.add(this.warmup.mesh); }
  prepared() { this.warmup.mesh.removeFromParent(); }
  receive(event: CombatVisualEvent, at: number) {
    if ((event.kind === 'impact' || event.kind === 'miss') && event.attackId === 'shadow-blast') this.pending.push({ event, at });
  }
  update(snapshot: WorldSnapshot, earlier: WorldSnapshot, alpha: number, positions: ReadonlyMap<string, THREE.Object3D>, origins: ReadonlyMap<string, { position: THREE.Vector3 }>) {
    const time = earlier.gameTime + (snapshot.gameTime - earlier.gameTime) * alpha; this.lights.length = 0;
    const shots = new Map(snapshot.projectiles.filter(shot => shot.attackId === 'shadow-blast').map(shot => [shot.id, shot]));
    if (alpha < 1) for (const shot of earlier.projectiles) if (shot.attackId === 'shadow-blast' && !shots.has(shot.id)) shots.set(shot.id, { ...shot, remaining: 0 });
    for (const shot of shots.values()) {
      const before = earlier.projectiles.find(item => item.id === shot.id);
      const remaining = THREE.MathUtils.lerp(before?.remaining ?? shot.duration, shot.remaining, alpha);
      let flight = this.flights.get(shot.id);
      if (!flight) {
        const from = origins.get(shot.actorId)?.position.clone() ?? new THREE.Vector3(shot.from.x, this.groundHeight(shot.from.x, shot.from.z) + (this.heights.get(shot.actorId) ?? 2) * .76, shot.from.z);
        const smoke = new DarkSmoke(CORE + TRAIL, LOOK.color, LOOK.rim, 'shadow-blast-flight'), halo = coldHalo();
        flight = { smoke, halo, from, to: new THREE.Vector3(), head: from.clone(), trail: [], shed: time, random: mulberry32(shot.id * 7919), seed: (shot.id * .618) % 1 };
        this.scene.add(halo, smoke.mesh); this.flights.set(shot.id, flight);
      }
      flight.to.set(THREE.MathUtils.lerp(before?.to.x ?? shot.to.x, shot.to.x, alpha), 0, THREE.MathUtils.lerp(before?.to.z ?? shot.to.z, shot.to.z, alpha));
      flight.to.y = this.groundHeight(flight.to.x, flight.to.z) + (this.heights.get(shot.targetId) ?? 2) * .55;
      const p = THREE.MathUtils.clamp(1 - remaining / shot.duration, 0, 1);
      // A slight sag and weave: it is smoke, flung, not an arrow.
      flight.head.copy(flight.from).lerp(flight.to, p);
      flight.head.y += Math.sin(p * Math.PI) * .25;
      const weave = Math.sin(p * 9 + flight.seed * 6) * .06 * Math.sin(p * Math.PI);
      flight.head.x += weave; flight.head.z -= weave;
      this.fly(flight, time, Math.min(1, p * 8));
      this.lights.push({ position: flight.head, color: new THREE.Color(LOOK.rim), output: .05 });
    }
    for (const [id, flight] of this.flights) if (!shots.has(id)) {
      // The knot is gone into its burst; the wisps it shed hang on and thin.
      flight.ended ??= time;
      if (time - flight.ended > TRAIL_LIFE) { flight.smoke.dispose(); flight.halo.removeFromParent(); flight.halo.material.dispose(); this.flights.delete(id); }
      else this.fly(flight, time, 0);
    }
    let waiting = 0;
    for (const item of this.pending) { if (time + .001 >= item.at) this.burst(item.event, item.at, positions); else this.pending[waiting++] = item; } this.pending.length = waiting;
    this.bursts = this.bursts.filter(burst => {
      const p = (time - burst.at) / burst.duration;
      if (p >= 1) { burst.smoke.dispose(); burst.halo.removeFromParent(); burst.halo.material.dispose(); return false; }
      const target = positions.get(burst.targetId);
      if (!burst.miss && target?.visible) { burst.centre.x = target.position.x; burst.centre.z = target.position.z; }
      this.billow(burst, time);
      if (p < .4) this.lights.push({ position: burst.centre, color: new THREE.Color(LOOK.rim), output: .08 * (1 - p / .4) });
      return true;
    });
  }
  /** The knot at the head (`strength` 0 once it has landed) and the wisps it sheds along its way. */
  private fly(flight: Flight, time: number, strength: number) {
    const { smoke, head, random, halo } = flight;
    // A faint cold light round the knot, so the dark reads against dark ground.
    halo.visible = strength > 0; halo.position.copy(head); halo.scale.setScalar(LOOK.radius * 4.2 * strength);
    halo.material.opacity = .4 * strength * (.85 + .15 * Math.sin(time * 23));
    if (strength > 0) while (flight.shed < time) {
      flight.shed += 1 / SHED;
      flight.trail.push({ x: head.x + (random() - .5) * .12, y: head.y + (random() - .5) * .12, z: head.z + (random() - .5) * .12,
        vx: (random() - .5) * .6, vy: .15 + random() * .35, vz: (random() - .5) * .6, born: flight.shed, life: TRAIL_LIFE * (.6 + random() * .4), size: .26 + random() * .2, seed: random() });
      if (flight.trail.length > TRAIL) flight.trail.shift();
    }
    for (let i = 0; i < CORE; i++) {
      if (strength <= 0) { smoke.hide(i); continue; }
      // Lumps tumbling about the heart of the knot, the outer ones lit at their rims.
      const a = time * (5 + i) + i * 2.4, r = LOOK.radius * (.15 + .35 * ((i * .37) % 1));
      smoke.set(i, head.x + Math.cos(a) * r, head.y + Math.sin(a * 1.3) * r * .8, head.z + Math.sin(a) * r,
        LOOK.radius * (1.5 - i * .1) * strength, .95, i < 2 ? .2 : .8, time * (.3 + i * .1), i * .29 + flight.seed);
    }
    flight.trail.forEach((puff, k) => {
      const age = time - puff.born, q = age / puff.life;
      if (q >= 1 || q < 0) { smoke.hide(CORE + k); return; }
      const drag = 1 - Math.exp(-age * 3);
      smoke.set(CORE + k, puff.x + puff.vx * drag / 3, puff.y + puff.vy * age, puff.z + puff.vz * drag / 3,
        puff.size * (1 + q * 2.2), .85 * (1 - q) ** 1.4, .5, puff.seed + age * .3, puff.seed);
    });
    for (let k = flight.trail.length; k < TRAIL; k++) smoke.hide(CORE + k);
    smoke.commit(time);
  }
  private burst(event: CombatVisualEvent, at: number, positions: ReadonlyMap<string, THREE.Object3D>) {
    const random = mulberry32(event.id * 104729), miss = event.kind === 'miss';
    const height = this.heights.get(event.targetId) ?? 2, target = positions.get(event.targetId);
    const centre = new THREE.Vector3(target?.position.x ?? event.to.x, 0, target?.position.z ?? event.to.z);
    centre.y = this.groundHeight(centre.x, centre.z) + height * .55;
    const along = new THREE.Vector3(event.to.x - event.from.x, 0, event.to.z - event.from.z);
    if (along.lengthSq() < 1e-4) along.set(0, 0, 1); along.normalize();
    // A miss goes on past, off the target's flank, and breaks up there.
    if (miss) centre.addScaledVector(along, 1.4).addScaledVector(new THREE.Vector3(-along.z, 0, along.x), event.id % 2 ? .5 : -.5);
    const puffs = Array.from({ length: miss ? Math.round(BURST * .6) : BURST }, (): Puff => {
      const angle = random() * Math.PI * 2, up = random() * 2 - .6, speed = (miss ? 1 : 1.6) + random() * 1.8;
      // Thrown out round the target, most of it onward, the way the blast was going.
      const dir = new THREE.Vector3(Math.cos(angle), up * .6, Math.sin(angle)).addScaledVector(along, miss ? 1.4 : .7).normalize();
      return { x: centre.x, y: centre.y, z: centre.z, vx: dir.x * speed, vy: dir.y * speed + .4, vz: dir.z * speed, born: at + random() * .06, life: (miss ? LOOK.miss : LOOK.impact) * (.65 + random() * .35), size: .22 + random() * .22, seed: random() };
    });
    // Tendrils that rise up round the target and clutch inward at it.
    const clutch = miss ? [] : Array.from({ length: CLUTCH }, (_, i): Puff => {
      const angle = i / CLUTCH * Math.PI * 2 + random() * .4;
      return { x: Math.cos(angle), y: -.5 - random() * .3, z: Math.sin(angle), vx: 0, vy: 0, vz: 0, born: at + .05 + random() * .15, life: .55 + random() * .2, size: .3 + random() * .12, seed: random() };
    });
    const smoke = new DarkSmoke(puffs.length + clutch.length, LOOK.color, LOOK.rim, 'shadow-blast-burst'), halo = coldHalo();
    halo.position.copy(centre); this.scene.add(halo, smoke.mesh);
    this.bursts.push({ smoke, halo, targetId: event.targetId, at, centre, puffs, clutch, miss, duration: Math.max(...puffs.map(puff => puff.born - at + puff.life), ...clutch.map(puff => puff.born - at + puff.life)) });
  }
  private billow(burst: Burst, time: number) {
    const { smoke, centre, puffs, clutch, halo } = burst;
    const flash = Math.max(0, 1 - (time - burst.at) / .35);
    halo.position.copy(centre); halo.scale.setScalar(2.4 - flash * .8); halo.material.opacity = .45 * flash * flash; halo.visible = flash > 0;
    puffs.forEach((puff, i) => {
      const age = time - puff.born, q = age / puff.life;
      if (q < 0 || q >= 1) { smoke.hide(i); return; }
      // Hard out, then caught by the air: it slows, swells and drifts up as it thins.
      const travel = (1 - Math.exp(-age * 4.5)) / 4.5;
      smoke.set(i, puff.x + puff.vx * travel, puff.y + puff.vy * travel + age * .35, puff.z + puff.vz * travel,
        puff.size * (1 + q * 2.6), .92 * Math.min(1, age * 30) * (1 - q) ** 1.3, .65 - q * .4, puff.seed + age * .4, puff.seed);
    });
    const height = this.heights.get(burst.targetId) ?? 2;
    clutch.forEach((puff, k) => {
      const i = puffs.length + k, age = time - puff.born, q = age / puff.life;
      if (q < 0 || q >= 1) { smoke.hide(i); return; }
      // Each rises from low at the target's side and closes on its chest.
      const close = 1 - (1 - Math.min(1, q * 1.6)) ** 2, radius = THREE.MathUtils.lerp(.85, .12, close);
      const angle = Math.atan2(puff.z, puff.x) + q * .9;
      smoke.set(i, centre.x + Math.cos(angle) * radius, centre.y + height * THREE.MathUtils.lerp(puff.y * .5, .05, close), centre.z + Math.sin(angle) * radius,
        puff.size * (1.1 - close * .5), .85 * Math.sin(Math.min(1, q * 1.15) * Math.PI), .7, puff.seed + q, puff.seed);
    });
    smoke.commit(time);
  }
  clear() {
    for (const item of [...this.flights.values(), ...this.bursts]) { item.smoke.dispose(); item.halo.removeFromParent(); item.halo.material.dispose(); }
    this.flights.clear(); this.bursts = []; this.pending = []; this.lights.length = 0;
  }
  dispose() { this.clear(); this.warmup.dispose(); }
}

function coldHalo() {
  const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: softGlowTexture(), color: LOOK.glow, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, opacity: 0 }));
  halo.name = 'shadow-blast-halo'; halo.renderOrder = 2;
  return halo;
}
